import Image from 'next/image';
import { useMemo } from 'react';
import { useCurrency } from 'src/modules/currencies/hooks/useCurrency';

import { Badge, KeyValueRow, KeyValueTable } from '@/design-system/components/ui';

import { Trigger } from '../../domain';
import { AddressChip } from './components/AddressChip';

interface Props {
  trigger: Trigger;
}

export const SolanaTokenHoldersTriggerFullDetails = ({ trigger }: Props) => {
  const tokenAddress = trigger.context?.token_address ?? '';
  const volumeCurrencyExpression = trigger.context?.volume_currency_expression;

  const contractDisplay = (
    <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
      <AddressChip
        address={tokenAddress}
        scannerUrl={`https://explorer.solana.com/address/${tokenAddress}`}
        hideChainIcon
      />
    </div>
  );

  const networkDisplay = (
    <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
      <Image src="/assets/webp/solana.webp" alt="Solana" width={20} height={20} />
      <span>Solana</span>
    </div>
  );

  const { data: volumeCurrency } = useCurrency(volumeCurrencyExpression, '101', !!volumeCurrencyExpression);

  const volumeCurrencyDisplay = useMemo(() => {
    if (!volumeCurrencyExpression) return '-';

    const symbol = volumeCurrency?.name ?? volumeCurrencyExpression;
    const logoURI = volumeCurrency?.logoUri ?? '/assets/webp/solana.webp';

    return (
      <Badge size="sm">
        <Image src={logoURI} alt={symbol} width={16} height={16} />
        {symbol}
      </Badge>
    );
  }, [volumeCurrencyExpression, volumeCurrency]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Executed with" value="Snapshots of Solana token holders for contract">
        <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center tw:flex-wrap tw:ml-2">{contractDisplay}</div>
      </KeyValueRow>
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow label="Volume currency" value={volumeCurrencyDisplay} />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
};
